import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import Button from "components/button";
import Input, { Textarea } from "components/input";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditTripTypeForm from "./useEditTripTypeForm";

export const editTripTypePageRouteName = (id?: string) =>
  `/config/trip-type/${id ?? ":id"}/edit`;

const EditTripTypePage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    handleSubmit,
    errors,
    register,
    control,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  } = useEditTripTypeForm(id);

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <Input
            label={t("label-trip-type-name")}
            placeholder={t("label-trip-type-name")}
            required
            {...register("tripTypeName")}
            error={errors.tripTypeName}
            data-testid="input-trip-type-name-edit"
          />

          <Textarea
            label={t("label-description")}
            placeholder={t("label-description")}
            {...register("description")}
            error={errors.description}
            data-testid="textarea-trip-type-description-edit"
          />

          <Controller
            name="isFreezer"
            control={control}
            render={({ field }) => (
              <div className="flex items-center justify-between">
                <Typography variant="bodyLarge">
                  {t("label-is-freezer")}
                </Typography>
                <button
                  type="button"
                  role="switch"
                  aria-checked={field.value}
                  onClick={() => field.onChange(!field.value)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                    field.value ? "bg-primary-600" : "bg-greyScale-60"
                  }`}
                  data-testid="toggle-is-freezer-trip-type-edit"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      field.value ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            )}
          />
        </form>
      </section>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-trip-type-edit"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSubmit(onSubmit)}
          data-testid="btn-save-trip-type-edit"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-trip-type-edit-modal"
      />

      {/* Discard Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="discard-trip-type-edit-modal"
      />
    </div>
  );
};

export default EditTripTypePage;
